import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const tabsTriggerVariants = cva(
  `inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50`,
  {
    variants: {
      variant: {
        default: `data-[state=active]:bg-blue-100 data-[state=active]:text-foreground data-[state=active]:shadow-sm`,
        underline: `border-b-2 border-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 bg-transparent hover:text-blue-600 hover:border-blue-600 hover:border-b-2`,
        underlineWhite: `bg-white border-b-2 border-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 bg-transparent hover:text-blue-600 hover:border-blue-600 hover:border-b-2`,
        underlineTransparent: `bg-transparent border-b-2 border-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:bg-transparent data-[state=active]:text-blue-600  bg-transparent hover:text-blue-600 hover:border-blue-600 hover:border-b-2`,
      },
    },
    defaultVariants: {
      variant: `default`,
    },
  }
)

type TabsTriggerVariant = VariantProps<typeof tabsTriggerVariants>["variant"]

const Tabs = TabsPrimitive.Root

const TabsList = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithRef<typeof TabsPrimitive.List>) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      `inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground`,
      className
    )}
    {...props}
  />
)
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = ({
  ref,
  className,
  variant,
  ...props
}: React.ComponentPropsWithRef<typeof TabsPrimitive.Trigger> & {
  variant?: TabsTriggerVariant
}) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(tabsTriggerVariants({ variant }), className)}
    {...props}
  />
)
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithRef<typeof TabsPrimitive.Content>) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      `mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`,
      className
    )}
    {...props}
  />
)
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
